import {
  getListSortOptions,
  getProductActivationStatusLabel as getProductActivationStatusLabelFromEnum,
  getProductTypeLabel as getProductTypeLabelFromEnum,
  getProductVerificationStatusLabel as getProductVerificationStatusLabelFromEnum,
} from '_i18n/enumLabels';
import { ITab } from 'types/globalTypes';
import {
  IAddProduct,
  PRODUCT_ACTIVATION_STATUS,
  PRODUCT_TYPES,
  PRODUCT_VERIFICATION_STATUS,
} from 'types/marketpleace/product.interface';
import { enumToSelectOptions } from 'utils/baseUtils';
import ProcessingIco from '../../../../assets/icons/status/processing.svg';
import HoldIco from '../../../../assets/icons/status/hold.svg';
import VerifiedIco from '../../../../assets/icons/status/verified.svg';
import { Filter } from 'components/filters/NewFilterPanel';
import { getCategoriesTabFilterValues } from 'middleware/category/getCategories';
import { getFilterTabsByEnum } from 'utils/objectUtils';
import { TFunction } from 'i18next';
import { ISelectOption } from 'components/common/inputs/inputTypes';

export const getProductTypes = (
  t: TFunction,
): Array<ISelectOption<PRODUCT_TYPES>> =>
  enumToSelectOptions(PRODUCT_TYPES).map((option) => ({
    ...option,
    value: option.value as unknown as PRODUCT_TYPES,
    label: getProductTypeLabel(option.value as unknown as PRODUCT_TYPES, t),
  }));

export const getSorts = (t: TFunction) => getListSortOptions(t);

export const productInitialValues: IAddProduct = {
  Visibility: 1,
  Name: '',
  Description: '',
  ShortDescription: '',
  BannerPhotoFilePath: '',
  Tag: '',
  PhotoFilesPath: [''],
  Type: 0,
  CategoryId: '',
  AcademyId: '',
};

export const getProductHeaders = (t: TFunction): string[] => [
  t('common.name'),
  t('shop.category'),
  t('shop.productType'),
  t('shop.stock'),
  t('shop.availabilityInShop'),
  t('common.status'),
];

export const getProductTypeLabel = (
  type: PRODUCT_TYPES,
  t: TFunction,
): string => getProductTypeLabelFromEnum(t, type);

export const getVerificationStatusLabel = (
  status: PRODUCT_VERIFICATION_STATUS,
  t: TFunction,
): string => getProductVerificationStatusLabelFromEnum(t, status);

export const getActivationStatusLabel = (
  status: PRODUCT_ACTIVATION_STATUS,
  t: TFunction,
): string => getProductActivationStatusLabelFromEnum(t, status);

export const getIconSrcByProductStatus = (
  status: PRODUCT_VERIFICATION_STATUS
): string => {
  switch (status) {
    case 0:
      return ProcessingIco;

    case 1:
      return VerifiedIco;

    case 2:
      return HoldIco;
    default:
      return '';
  }
};

export enum PRODUCT_DETAIL_TABS {
  supplementaryData = 'supplementaryData',
  attributes = 'attributes',
  conditions = 'conditions',
  raport = 'raport',
  documents = 'documents',
}

export const getProductDetailTabs = (t: TFunction): Array<ITab> => [
  { id: PRODUCT_DETAIL_TABS.supplementaryData, label: t('shop.supplementaryData') },
  { id: PRODUCT_DETAIL_TABS.attributes, label: t('shop.attributes') },
  { id: PRODUCT_DETAIL_TABS.conditions, label: t('shop.conditions') },
  { id: PRODUCT_DETAIL_TABS.raport, label: t('shop.report') },
  { id: PRODUCT_DETAIL_TABS.documents, label: t('shop.documents') },
];

export const getProductFilters = async (
  academyId: string,
  t: TFunction,
): Promise<Filter[]> => [
  {
    id: 0,
    name: 'CategoryId',
    pl: t('shop.category'),
    type: 'select',
    values: await getCategoriesTabFilterValues(academyId),
  },
  {
    id: 1,
    name: 'Type',
    pl: t('shop.productType'),
    type: 'select',
    values: getFilterTabsByEnum(PRODUCT_TYPES).map((value) => ({
      ...value,
      pl: getProductTypeLabel(Number(value.backend) as PRODUCT_TYPES, t),
    })),
  },
  {
    id: 2,
    name: 'Status',
    pl: t('common.status'),
    type: 'select',
    values: getFilterTabsByEnum(PRODUCT_VERIFICATION_STATUS).map((value) => ({
      ...value,
      pl: getVerificationStatusLabel(
        Number(value.backend) as PRODUCT_VERIFICATION_STATUS,
        t,
      ),
    })),
  },
];

///Home table helpers

export const compare = <T>(
  a: T,
  b: T,
  key: keyof T,
  order: 'asc' | 'desc'
): number => {
  const valA = a[key];
  const valB = b[key];

  if (valA == null && valB == null) return 0;
  if (valA == null) return order === 'asc' ? -1 : 1;
  if (valB == null) return order === 'asc' ? 1 : -1;

  if (typeof valA === 'string' && typeof valB === 'string') {
    return order === 'asc'
      ? valA.localeCompare(valB)
      : valB.localeCompare(valA);
  }

  return order === 'asc'
    ? (valA as number) - (valB as number)
    : (valB as number) - (valA as number);
};

export const calcTrends = (
  current: number | string | null | undefined,
  previous: number | string | null | undefined,
  fractionDigits: number = 0
): number | undefined => {
  const currentNum = Number(current);
  const previousNum = Number(previous);

  if (
    current === null ||
    current === undefined ||
    previous === null ||
    previous === undefined ||
    Number.isNaN(currentNum) ||
    Number.isNaN(previousNum)
  ) {
    return undefined;
  }

  if (previousNum === 0) {
    return currentNum === 0 ? 0 : undefined;
  }

  return Number((currentNum - previousNum).toFixed(fractionDigits));
};

const parseValue = (val: any): number =>
  typeof val === 'string' ? parseFloat(val.replace(',', '.')) : Number(val);

export const getPlayerTrends = (player30: any, player7: any) => {
  return {
    Form: calcTrends(parseValue(player7.Form), parseValue(player30.Form)),
    Readiness: calcTrends(
      parseValue(player7.Readiness),
      parseValue(player30.Readiness)
    ),
    Potential: calcTrends(
      parseValue(player7.Potential),
      parseValue(player30.Potential)
    ),

    EventCountNLastDays: calcTrends(
      parseValue(player7.EventCountNLastDays),
      parseValue(player30.EventCountNLastDays)
    ),
    EventTotalTimeInMinutes: calcTrends(
      parseValue(player7.EventTotalTimeInMinutes),
      parseValue(player30.EventTotalTimeInMinutes)
    ),

    MaxSpeedNLastDays: calcTrends(
      parseValue(player7.MaxSpeedNLastDays),
      parseValue(player30.MaxSpeedNLastDays),
      2
    ),
    TotalDistance: calcTrends(
      parseValue(player7.TotalDistance),
      parseValue(player30.TotalDistance)
    ),
    AverageDistance: calcTrends(
      parseValue(player7.AverageDistance),
      parseValue(player30.AverageDistance)
    ),

    RunDistanceTotal: calcTrends(
      parseValue(player7.RunDistanceTotal),
      parseValue(player30.RunDistanceTotal)
    ),
    RunDistancePercentage: calcTrends(
      parseValue(player7.RunDistancePercentage),
      parseValue(player30.RunDistancePercentage)
    ),

    HSRDistanceTotal: calcTrends(
      parseValue(player7.HSRDistanceTotal),
      parseValue(player30.HSRDistanceTotal)
    ),
    HSRDistancePercentage: calcTrends(
      parseValue(player7.HSRDistancePercentage),
      parseValue(player30.HSRDistancePercentage)
    ),

    SprintDistanceTotal: calcTrends(
      parseValue(player7.SprintDistanceTotal),
      parseValue(player30.SprintDistanceTotal)
    ),
    SprintDistancePercentage: calcTrends(
      parseValue(player7.SprintDistancePercentage),
      parseValue(player30.SprintDistancePercentage)
    ),

    SprintNumberTotal: calcTrends(
      parseValue(player7.SprintNumberTotal),
      parseValue(player30.SprintNumberTotal)
    ),
    TimeToSprintInSeconds: calcTrends(
      parseValue(player7.TimeToSprintInSeconds),
      parseValue(player30.TimeToSprintInSeconds)
    ),

    HMLDDistance: calcTrends(
      parseValue(player7.HMLDDistance),
      parseValue(player30.HMLDDistance)
    ),
    HMLDDistancePercentage: calcTrends(
      parseValue(player7.HMLDDistancePercentage),
      parseValue(player30.HMLDDistancePercentage)
    ),

    AccelerationNumberTotal: calcTrends(
      parseValue(player7.AccelerationNumberTotal),
      parseValue(player30.AccelerationNumberTotal)
    ),
    DecelerationNumberTotal: calcTrends(
      parseValue(player7.DecelerationNumberTotal),
      parseValue(player30.DecelerationNumberTotal)
    ),

    MaxPowerOutput: calcTrends(
      parseValue(player7.MaxPowerOutput),
      parseValue(player30.MaxPowerOutput)
    ),
    MaxHeartRate: calcTrends(
      parseValue(player7.MaxHeartRate),
      parseValue(player30.MaxHeartRate)
    ),
    HeartRateLastNDaysAvg: calcTrends(
      parseValue(player7.HeartRateLastNDaysAvg),
      parseValue(player30.HeartRateLastNDaysAvg)
    ),

    HeartRate1ZonePercentage: calcTrends(
      parseValue(player7.HeartRate1ZonePercentage),
      parseValue(player30.HeartRate1ZonePercentage)
    ),
    HeartRate2ZonePercentage: calcTrends(
      parseValue(player7.HeartRate2ZonePercentage),
      parseValue(player30.HeartRate2ZonePercentage)
    ),
    HeartRate3ZonePercentage: calcTrends(
      parseValue(player7.HeartRate3ZonePercentage),
      parseValue(player30.HeartRate3ZonePercentage)
    ),
    HeartRate4ZonePercentage: calcTrends(
      parseValue(player7.HeartRate4ZonePercentage),
      parseValue(player30.HeartRate4ZonePercentage)
    ),
    HeartRate5ZonePercentage: calcTrends(
      parseValue(player7.HeartRate5ZonePercentage),
      parseValue(player30.HeartRate5ZonePercentage)
    ),

    HRR: calcTrends(parseValue(player7.HRR), parseValue(player30.HRR)),

    DynamicPercentage: calcTrends(
      parseValue(player7.DynamicPercentage),
      parseValue(player30.DynamicPercentage)
    ),
    EnergyPercentage: calcTrends(
      parseValue(player7.EnergyPercentage),
      parseValue(player30.EnergyPercentage)
    ),
    EndurancePercentage: calcTrends(
      parseValue(player7.EndurancePercentage),
      parseValue(player30.EndurancePercentage)
    ),
    IntensityPercentage: calcTrends(
      parseValue(player7.IntensityPercentage),
      parseValue(player30.IntensityPercentage)
    ),
  };
};

type TrendResult = {
  percentage?: number;
  direction?: 'up' | 'down' | 'no change';
};

export const calcTrendWithDirection = (
  current: number | string | null | undefined,
  previous: number | string | null | undefined
): TrendResult => {
  const parseNumber = (
    value: number | string | null | undefined
  ): number | null => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'number') return value;
    const num = Number(value.toString().replace(',', '.'));
    return Number.isNaN(num) ? null : num;
  };

  const curr = parseNumber(current);
  const prev = parseNumber(previous);

  if (curr === null || prev === null || prev === 0) {
    return { percentage: undefined, direction: undefined };
  }

  const percentage = Number(
    (((curr - prev) / Math.abs(prev)) * 100).toFixed(0)
  );
  const direction = curr > prev ? 'up' : curr < prev ? 'down' : 'no change';

  return { percentage, direction };
};

export const formattedValue = (numericValue: number | string) =>
  numericValue.toLocaleString('pl-PL', {
    maximumFractionDigits: 3,
    minimumFractionDigits: 0,
    useGrouping: true,
  });

export const getArrowStyle = (
  activeField: string,
  currentField: string,
  sortDirection: string,
  baseStyle: any = {}
) => {
  const isActive = activeField === currentField;
  return {
    ...baseStyle,
    transform: isActive
      ? sortDirection === 'asc'
        ? 'rotate(0deg)'
        : 'rotate(180deg)'
      : 'rotate(0deg)',
    transition: 'transform 0.2s ease',
    opacity: isActive ? (sortDirection === 'asc' ? 0.6 : 1) : 1,
  };
};

export const RowsName: Record<string, keyof any> = {
  'task number': 'EventCountNLastDays',
  'task min': 'EventTotalTimeInMinutes',
  VMAX: 'MaxSpeedNLastDays',
  'distance total': 'TotalDistance',
  'distance Avg': 'AverageDistance',
  'hsr percentage': 'HSRDistancePercentage',
  'hsr m': 'HSRDistanceTotal',
  'hmld percentage': 'HMLDDistancePercentage',
  'hmld m': 'HMLDDistance',
  'Average HeartRate': 'HeartRateLastNDaysAvg',
  'Max HeartRate': 'MaxHeartRate',
  poz: 'PreferredPosition',
  Age: 'Age',
  PHV: 'PHV',
  'run m': 'RunDistanceTotal',
  'run %': 'RunDistancePercentage',
  'sprint m': 'SprintDistanceTotal',
  'sprint %': 'SprintDistancePercentage',
  'sprint L': 'SprintNumberTotal',
  tts: 'TimeToSprintInSeconds',
  'power max': 'MaxPowerOutput',
  'piz acceleration': 'AccelerationNumberTotal',
  'piz deceleration': 'DecelerationNumberTotal',
  hr1: 'HeartRate1ZonePercentage',
  hr2: 'HeartRate2ZonePercentage',
  hr3: 'HeartRate3ZonePercentage',
  hr4: 'HeartRate4ZonePercentage',
  hr5: 'HeartRate5ZonePercentage',
  dynamika: 'DynamicPercentage',
  wytrzymałość: 'EndurancePercentage',
  intensity: 'IntensityPercentage',
  hrr: 'HRR',
  forma: 'Form',
  gotowość: 'Readiness',
  potencjał: 'Potential',
  kalorie: 'BurnCalories',
  playerLoad:'Load'
};
